import {
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
  FC,
  memo,
  ChangeEvent,
  KeyboardEvent,
} from 'react';
import styled from 'styled-components';
import {
  Alert,
  Stack,
  TextareaAutosize,
  Typography,
  Tooltip,
  IconButton,
  useTheme,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import {
  useAppDispatch,
  useAppSelector,
  chatActions,
  chatSelectors,
  useSetAssociateIsTyping,
} from '@vs-online-only-app/state';
import {
  useClickOutside,
  useScreenSize,
  useNetworkConnectionStatus,
} from '@vs-online-only-app/ui';
import MessageComposerUploadedFiles from './MessageComposerUploadedFiles';
import MessageComposerSharedProducts from './MessageComposerSharedProducts';
import MessageComposerFileUploadButton from './MessageComposerFileUploadButton';
import { MessageComposerNetworkErrorBanner } from './MessageComposerNetworkErrorBanner';
import useSendConversationMessages from '../../hooks/useSendConversationMessages';
import MessageComposerProductCatalog from './MessageComposerProductCatalog';
import { useSelectedHeroConversation } from '../../hooks/useSelectedHeroConversation';
import MessageComposerSavedReplies from './MessageComposerSavedReplies';
import useHandlePasteUrl from './hooks/useHandlePastedUrl';
import { CircularProgress } from '@mui/material';

const TextArea = styled(TextareaAutosize)`
  resize: none;
  outline: none;
  border: none;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  cursor: text;

  ${({ theme }) => theme.typography.body1};
`;

const Wrapper = styled.div<{ $isFocused: boolean }>`
  // 50% of the screen height minus spacer above TextArea (2 spacing) and parent padding-bottom (3 spacing)
  max-height: calc(50vh - ${({ theme }) => theme.spacing(5)});
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.spacing(2)};
  border-width: 1px;
  border-style: solid;
  border-color: ${({ $isFocused, theme }) =>
    $isFocused ? theme.palette.primary.main : theme.palette.divider};
  padding: ${({ theme }) => theme.spacing(2, 2, 1)};
  transition: border-color 0.2s ease;

  &:active {
    border-color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const MAX_ALLOWED_FILES = 10;
const MAX_ALLOWED_FILE_SIZE_MB = 150;

export function usePrevious<T>(
  value: T,
): MutableRefObject<T | undefined>['current'] {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

const MessageComposer: FC = () => {
  const dispatch = useAppDispatch();
  const [value, setValue] = useState<string>('');
  const [hasFilesizeError, setHasFilesizeError] = useState<boolean>(false);
  const [isNetworkErrorVisible, setIsNetworkErrorVisible] =
    useState<boolean>(false);
  const [isSendingMessages, setIsSendingMessages] = useState<boolean>(false);
  const selectedSavedReplyContent = useAppSelector(
    chatSelectors.selectSelectedSavedReplyContent,
  );
  const theme = useTheme();
  const screenSize = useScreenSize();
  const selectedConversation = useSelectedHeroConversation();
  const setAssociateTyping = useSetAssociateIsTyping(
    selectedConversation?.twilioConversationId,
  );
  const { handlePaste } = useHandlePasteUrl({
    conversationId: selectedConversation?.id,
    setValue,
  });

  const isOnline = useNetworkConnectionStatus();

  useEffect(() => {
    setIsNetworkErrorVisible(!isOnline);
  }, [isOnline]);

  const isSavedRepliesModalOpen = useAppSelector(
    chatSelectors.selectSavedRepliesModalOpen,
  );

  const wasSavedRepliesModalOpen = usePrevious(isSavedRepliesModalOpen);

  useEffect(() => {
    if (!selectedSavedReplyContent) return;
    setValue(selectedSavedReplyContent);
  }, [wasSavedRepliesModalOpen, selectedSavedReplyContent]);

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const toolbarLeftRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useClickOutside(() => {
    setIsFocused(false);
  });
  const uploadedFiles = useAppSelector((state) =>
    selectedConversation
      ? chatSelectors.selectUploadedFilesByConversationId(
          state,
          selectedConversation.id,
        )
      : [],
  );

  const sharedProducts = useAppSelector((state) =>
    selectedConversation
      ? chatSelectors.selectConversationSharedProducts(
          state,
          selectedConversation.id,
        )
      : [],
  );

  useEffect(() => {
    if (hasFilesizeError) {
      setTimeout(() => setHasFilesizeError(false), 5000);
    }
  }, [hasFilesizeError]);

  const { sendMessages } = useSendConversationMessages(selectedConversation);

  useEffect(() => {
    if (textareaRef.current) {
      setIsFocused(true);
      textareaRef.current.focus();
    }
  }, [selectedConversation]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const handleInsideMessageComposerClick = (event: {
      target: HTMLDivElement | null;
    }) => {
      if (
        event.target !== toolbarLeftRef.current &&
        !toolbarLeftRef.current?.contains(event.target)
      ) {
        textareaRef.current?.focus();
      }
    };

    wrapper.addEventListener('click', handleInsideMessageComposerClick);
    return () => {
      wrapper.removeEventListener('click', handleInsideMessageComposerClick);
    };
  }, [wrapperRef]);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
    setAssociateTyping.mutateAsync();
  };

  const handleSend = () => {
    if (isSendingDisabled) {
      return;
    }

    setIsSendingMessages(true);
    sendMessages({
      textMessageBody: value,
      files: uploadedFiles,
      sharedProducts,
    }).finally(() => {
      setIsSendingMessages(false);
    });
    setValue('');
    dispatch(chatActions.clearSelectedSavedReplyContent());
    if (uploadedFiles.length && selectedConversation) {
      dispatch(
        chatActions.clearConversationUploadedFiles(selectedConversation.id),
      );
    }
    if (sharedProducts.length && selectedConversation) {
      dispatch(
        chatActions.clearConversationSharedProducts(selectedConversation.id),
      );
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!isSendingDisabled) {
        handleSend();
      }

      if (!isOnline) {
        setIsNetworkErrorVisible(true);
      }
    }
  };

  const handleMediaSubmit = (files: File[]) => {
    const filesWithinAllowedSize = files.filter(
      (file) => file.size / (1024 * 1024) <= MAX_ALLOWED_FILE_SIZE_MB,
    );

    setHasFilesizeError(filesWithinAllowedSize.length !== files.length);

    if (selectedConversation) {
      dispatch(
        chatActions.addConversationUploadedFiles({
          conversationId: selectedConversation.id,
          files: files
            .map((file) => ({
              size: file.size,
              type: file.type,
              url: URL.createObjectURL(file),
            }))
            .slice(0, MAX_ALLOWED_FILES - uploadedFiles.length),
        }),
      );

      textareaRef.current?.focus();
    }
  };

  const handleFocus = () => setIsFocused(true);

  const isSendingDisabled = useMemo(() => {
    if (isSendingMessages) return true;
    const filesWithinAllowedSize = uploadedFiles.filter(
      (file) => file.size / (1024 * 1024) <= MAX_ALLOWED_FILE_SIZE_MB,
    );
    const trimmedValue = value?.trim();
    const hasSizeError = filesWithinAllowedSize.length !== uploadedFiles.length;
    return (
      !isOnline ||
      hasSizeError ||
      (!trimmedValue && !uploadedFiles.length && !sharedProducts.length)
    );
  }, [isOnline, value, uploadedFiles, sharedProducts, isSendingMessages]);

  const isUploadDisabled = useMemo(
    () => uploadedFiles.length >= MAX_ALLOWED_FILES,
    [uploadedFiles],
  );

  const handleNetworkErrorBannerClose = () => {
    setIsNetworkErrorVisible(false);
  };

  const placeholder = `Message ${selectedConversation?.conversationStarter.firstName}...`;

  const halfTheScreenHeight = screenSize.height / 2;

  const textAreaMaxRows = useMemo(() => {
    // Convert line height from rem to px
    const lineHeight = Number(theme.typography.body1.lineHeight) * 16;
    const padding = 50;
    return Math.floor((halfTheScreenHeight - padding) / lineHeight);
  }, [theme, halfTheScreenHeight]);

  // Textarea and the shared products Urls list should have similar max-height
  const sharedProductsUrlsHeight = (halfTheScreenHeight - 160) / 2; // 160 = padding(24) + spacer(8) + shared products(84) + icons(44)

  return (
    <>
      <Wrapper
        $isFocused={isFocused}
        ref={wrapperRef}
        onClick={() => {
          setIsFocused(true);
        }}
      >
        <TextArea
          ref={textareaRef}
          onChange={handleChange}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          autoFocus
          placeholder={placeholder}
          minRows={1}
          value={value}
          maxRows={textAreaMaxRows}
        />
        <MessageComposerSharedProducts maxHeight={sharedProductsUrlsHeight} />
        {Boolean(uploadedFiles.length) && selectedConversation && (
          <MessageComposerUploadedFiles
            conversationId={selectedConversation.id}
            files={uploadedFiles}
          />
        )}
        {(hasFilesizeError || uploadedFiles.length === MAX_ALLOWED_FILES) && (
          <Alert severity="error">
            {uploadedFiles.length === MAX_ALLOWED_FILES && (
              <>You can only send up to {MAX_ALLOWED_FILES} files at a time.</>
            )}
            {hasFilesizeError && (
              <>
                Some selected files are too large and cannot be uploaded. The
                limit is <strong>{MAX_ALLOWED_FILE_SIZE_MB} MB.</strong>
              </>
            )}
          </Alert>
        )}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack ref={toolbarLeftRef} direction="row" gap={1}>
            <Tooltip
              arrow
              title={
                <>
                  <Typography>Upload photos/videos</Typography>
                  <Typography sx={{ opacity: 0.7 }} variant="body2">
                    You can send up to {MAX_ALLOWED_FILES} at a time.
                  </Typography>
                </>
              }
            >
              <div>
                <MessageComposerFileUploadButton
                  title="Upload file"
                  onMediaSubmit={handleMediaSubmit}
                  isDisabled={isUploadDisabled}
                />
              </div>
            </Tooltip>
            <MessageComposerProductCatalog />
            <MessageComposerSavedReplies />
          </Stack>
          {isSendingMessages ? <CircularProgress size={24} /> : (
            <IconButton disabled={isSendingDisabled} onClick={handleSend}>
              <Send fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Wrapper>
      <MessageComposerNetworkErrorBanner
        open={isNetworkErrorVisible}
        onClose={handleNetworkErrorBannerClose}
      />
    </>
  );
};

export default memo(MessageComposer);
